import { motion } from "framer-motion";
import { ArrowRightLeft, Layers, ScanSearch, Check, ArrowRight } from "lucide-react";

const RACKS_IMG =
  "https://images.unsplash.com/photo-1592085198739-ffcad7f36b54?crop=entropy&cs=srgb&fm=jpg&q=85";
const AISLE_IMG =
  "https://images.unsplash.com/photo-1586528116022-aeda1613c63d?crop=entropy&cs=srgb&fm=jpg&q=85";

const CARD = {
  initial: { opacity: 0, y: 36 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, amount: 0.2 },
};

export default function Capabilities() {
  return (
    <section id="capabilities" className="relative py-24 sm:py-32">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 26 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.7 }}
          className="max-w-3xl"
        >
          <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-[#00F0FF]">
            03 — Core Capabilities
          </p>
          <h2 className="mt-4 font-display font-extrabold tracking-tight text-3xl sm:text-4xl lg:text-5xl text-white leading-[1.1]">
            Built to take you from manual to automated.
          </h2>
          <a
            href="/services"
            data-testid="capabilities-services-link"
            className="mt-6 inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.22em] text-[#00F0FF] hover:text-[#4DF7FF] transition-colors"
          >
            Explore services in detail <ArrowRight size={13} />
          </a>
        </motion.div>

        <div className="mt-14 grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-8">
          <motion.article
            {...CARD}
            transition={{ duration: 0.7 }}
            data-testid="capability-mrp-migration"
            className="sheen relative lg:col-span-7 h-full rounded-2xl border border-white/[0.07] bg-[#111827]/70 overflow-hidden hover:border-[#00F0FF]/35 transition-colors duration-500 group"
          >
            <img
              src={RACKS_IMG}
              alt="Automated warehouse racking"
              className="absolute right-0 top-0 h-full w-2/5 object-cover img-duotone opacity-30 group-hover:opacity-45 transition-opacity duration-700"
              loading="lazy"
            />
            <div className="absolute right-0 top-0 h-full w-2/5 bg-gradient-to-r from-[#111827] via-[#111827]/70 to-transparent" />
            <div className="relative p-7 sm:p-9 max-w-[62%] sm:max-w-[60%]">
              <span className="ghost-numeral font-display font-extrabold text-6xl absolute -top-2 right-4 select-none hidden sm:block" aria-hidden="true">
                01
              </span>
              <span className="h-11 w-11 rounded-xl border border-[#00F0FF]/25 bg-[#00F0FF]/[0.07] text-[#00F0FF] flex items-center justify-center">
                <ArrowRightLeft size={19} />
              </span>
              <h3 className="mt-5 font-display font-bold text-xl sm:text-2xl text-white tracking-tight">
                MRP &amp; System Migration
              </h3>
              <p className="mt-2 font-mono text-[10px] uppercase tracking-[0.2em] text-[#00F0FF]/80">
                Spreadsheets → automated planning tools
              </p>
              <p className="mt-4 text-sm sm:text-[15px] text-[#9CA3AF] leading-relaxed">
                Transitioning teams from basic spreadsheets to automated planning
                tools — data cleanup, ERP/MRP deployment and database configuration
                that bridges operational tracking with financial systems, without
                disrupting production.
              </p>
            </div>
          </motion.article>

          <motion.article
            {...CARD}
            transition={{ duration: 0.7, delay: 0.1 }}
            data-testid="capability-bom-optimization"
            className="sheen relative lg:col-span-5 h-full rounded-2xl border border-white/[0.07] bg-[#111827]/70 overflow-hidden hover:border-[#00F0FF]/35 transition-colors duration-500 group"
          >
            <span className="ghost-numeral font-display font-extrabold text-6xl absolute -top-2 right-4 select-none hidden sm:block" aria-hidden="true">
              02
            </span>
            <div className="relative p-7 sm:p-9">
              <span className="h-11 w-11 rounded-xl border border-[#00F0FF]/25 bg-[#00F0FF]/[0.07] text-[#00F0FF] flex items-center justify-center">
                <Layers size={19} />
              </span>
              <h3 className="mt-5 font-display font-bold text-xl sm:text-2xl text-white tracking-tight">
                Workflow &amp; BOM Optimization
              </h3>
              <p className="mt-2 font-mono text-[10px] uppercase tracking-[0.2em] text-[#00F0FF]/80">
                Multi-level Bills of Materials, precise
              </p>
              <p className="mt-4 text-sm sm:text-[15px] text-[#9CA3AF] leading-relaxed">
                Structuring multi-level Bills of Materials and tightening
                shop-floor inventory tracking — master data alignment so raw
                material availability always matches manufacturing output.
              </p>
            </div>
          </motion.article>

          <motion.article
            {...CARD}
            transition={{ duration: 0.7, delay: 0.15 }}
            data-testid="capability-operational-auditing"
            className="sheen relative lg:col-span-12 rounded-2xl border border-white/[0.07] bg-[#111827]/70 overflow-hidden hover:border-[#00F0FF]/35 transition-colors duration-500 group"
          >
            <img
              src={AISLE_IMG}
              alt="Warehouse aisle operations"
              className="absolute inset-0 w-full h-full object-cover img-duotone opacity-[0.14] group-hover:opacity-[0.22] transition-opacity duration-700"
              loading="lazy"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-[#111827] via-[#111827]/85 to-[#111827]/60" />
            <div className="relative p-7 sm:p-9 grid lg:grid-cols-2 gap-8 items-center">
              <div>
                <span className="h-11 w-11 rounded-xl border border-[#00F0FF]/25 bg-[#00F0FF]/[0.07] text-[#00F0FF] flex items-center justify-center">
                  <ScanSearch size={19} />
                </span>
                <h3 className="mt-5 font-display font-bold text-xl sm:text-2xl text-white tracking-tight">
                  Operational Auditing &amp; Control
                </h3>
                <p className="mt-2 font-mono text-[10px] uppercase tracking-[0.2em] text-[#00F0FF]/80">
                  Shrinkage down, discipline up
                </p>
                <p className="mt-4 text-sm sm:text-[15px] text-[#9CA3AF] leading-relaxed max-w-xl">
                  Reducing shrinkage, optimizing safety stock levels, and
                  establishing repeatable receiving and fulfillment processes —
                  backed by cycle count protocols and standard operating policies
                  that hold up to audit.
                </p>
              </div>
              <ul className="space-y-3.5 lg:pl-8 lg:border-l border-white/[0.08]">
                {[
                  "Shrinkage & variance root-cause analysis",
                  "Safety stock & reorder point optimization",
                  "Receiving, kitting & fulfillment SOPs",
                  "Vendor coordination & on-time delivery follow-through",
                ].map((item) => (
                  <li key={item} className="flex items-start gap-3 text-sm text-[#D1D5DB]">
                    <span className="mt-0.5 h-5 w-5 shrink-0 rounded-full border border-[#10B981]/40 bg-[#10B981]/10 text-[#10B981] flex items-center justify-center">
                      <Check size={11} />
                    </span>
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          </motion.article>
        </div>
      </div>
    </section>
  );
}
